import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { NgxGoogleMapsPlacesAutocompleteDirective } from 'ngx-google-maps-places-autocomplete';
import { FormErrorsConditionOperator } from '../../ui/enums/form-errors-condition-operator.enum';
import { UiModule } from '../../ui/ui.module';
import { GooglePlacesFormHelper } from '../google-places-form.helper';
import { GooglePlacesLoaderService } from '../google-places-loader.service';
import { GooglePlacesDetails, GooglePlacesFormFieldType } from '../google-places.model';

@Component({
  selector: 'ifc-google-places-autocomplete-form-field',
  templateUrl: './google-places-autocomplete-form-field.component.html',
  styleUrls: ['./google-places-autocomplete-form-field.component.scss'],
  standalone: true,
  imports: [
    UiModule,
    NgxGoogleMapsPlacesAutocompleteDirective,
  ],
})
export class GooglePlacesAutocompleteFormFieldComponent implements OnInit {
  @Input() control: FormControl;
  @Input() fieldType: GooglePlacesFormFieldType;
  @Input() cssClass?: string;

  @Output() placeSelected = new EventEmitter<GooglePlacesDetails>();
  
  readonly ConditionOperator = FormErrorsConditionOperator;

  isGooglePlacesApiLoaded$: Observable<boolean | null>;
  displayWithFn = GooglePlacesFormHelper.displayWithFn;
  debounceTime = GooglePlacesFormHelper.DEFAULT_DEBOUNCE_TIME;
  
  placesAutocompleteRequest: unknown;
  label: string;
  placeholder: string;
  fallbackPlaceholder: string;

  private initialValue: string | null = null;

  constructor(private readonly googlePlacesLoader: GooglePlacesLoaderService) {
    this.isGooglePlacesApiLoaded$ = this.googlePlacesLoader.isGooglePlacesApiLoaded$;
  }

  ngOnInit(): void {
    this.initData();
    this.preventInitialApiCall();
  }

  /**
   * Prevents unnecessary Google Places API calls on component initialization.
   * 
   * The ngx-google-maps-places-autocomplete library uses startWith(value) in its
   * valueChanges pipeline, which automatically triggers fetchSuggestions API call
   * when the FormControl has an initial value (e.g., loaded from database).
   * 
   * This method temporarily clears the control value before the library initializes,
   * then restores it after the library's afterNextRender callback completes.
   * This prevents wasting Google Places API quota on non-user interactions.
   * Feauture request submitted to library: https://github.com/lekhmanrus/ngx-google-maps-places/issues/15
   */
  private preventInitialApiCall(): void {
    if (this.control?.value) {
      this.initialValue = this.control.value;
      this.control.setValue('', { emitEvent: false });
    }

    setTimeout(() => {
      if (this.initialValue) {
        this.control.setValue(this.initialValue, { emitEvent: false });
        this.initialValue = null;
      }
    }, 100);
  }

  private initData(): void {
    switch (this.fieldType) {
      case GooglePlacesFormFieldType.Street:
        this.label = 'Street address';
        this.placeholder = 'Enter a location';
        this.fallbackPlaceholder = 'Enter street address';
        this.placesAutocompleteRequest = GooglePlacesFormHelper.STREET_AUTOCOMPLETE_REQUEST;
        break;
      case GooglePlacesFormFieldType.City:
        this.label = 'City';
        this.placeholder = 'Enter city';
        this.fallbackPlaceholder = 'Enter city';
        this.placesAutocompleteRequest = GooglePlacesFormHelper.CITY_AUTOCOMPLETE_REQUEST;
        break;
    }
  }

  handlePlaceDetailsLoad(placeDetails: GooglePlacesDetails): void {
    this.placeSelected.emit(placeDetails);
  }
}
